
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useStructuredData } from '@/components/seo/StructuredData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Loader2, AlertTriangle } from 'lucide-react';
import TallySurveyEmbed from '@/components/embeds/TallySurveyEmbed';

interface Service {
  id: string;
  title: string;
  description: string;
  detailed_description?: string;
  image_url?: string;
  service_type: string;
  service_status: 'active' | 'coming_soon' | 'inactive';
  sort_order: number;
  is_featured: boolean;
}

interface HealingStage {
  id: number;
  stage_number: number;
  title: string;
  subtitle?: string;
  description?: string;
  target_description?: string;
  color_theme: string;
  is_active: boolean;
  services: Service[];
}

interface StageAssignment {
  id: string;
  stage_id: number;
  service_id: string;
  sort_order: number;
}

const CertificationsList = () => {
  const { t, i18n } = useTranslation('certifications');
  const [stages, setStages] = useState<HealingStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getCertificationSchema } = useStructuredData();
  
  // Set up SEO with structured data
  useSEO({
    structuredData: [getCertificationSchema()]
  });

  // Helper function to get stage color classes
  const getStageColorClasses = (colorTheme: string) => {
    const colorMap: { [key: string]: string } = {
      red: 'border-red-200 bg-red-50/50',
      orange: 'border-orange-200 bg-orange-50/50',
      yellow: 'border-yellow-200 bg-yellow-50/50',
      green: 'border-green-200 bg-green-50/50',
      blue: 'border-blue-200 bg-blue-50/50',
      purple: 'border-purple-200 bg-purple-50/50'
    };
    return colorMap[colorTheme] || 'border-amber-100';
  };

  const getStageColorBg = (colorTheme: string) => {
    const colorMap: { [key: string]: string } = {
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500'
    };
    return colorMap[colorTheme] || 'bg-amber-600';
  };

  useEffect(() => {
    fetchStagesAndServices();
  }, []);

  const fetchStagesAndServices = async () => {
    try {
      setLoading(true);
      
      // Fetch stages
      const { data: stagesData, error: stagesError } = await supabase
        .from('healing_stages')
        .select('*')
        .eq('is_active', true)
        .order('stage_number', { ascending: true });

      if (stagesError) {
        console.error('Error fetching stages:', stagesError);
        setError('Failed to load healing stages');
        return;
      }

      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services_master')
        .select('*')
        .in('service_status', ['active', 'coming_soon'])
        .order('sort_order', { ascending: true });

      if (servicesError) {
        console.error('Error fetching services:', servicesError);
        setError('Failed to load services');
        return;
      }

      // Fetch assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('stage_service_assignments')
        .select('*')
        .order('sort_order', { ascending: true });

      if (assignmentsError) {
        console.error('Error fetching assignments:', assignmentsError);
        setError('Failed to load service assignments');
        return;
      }

      // Combine data
      const stagesWithServices: HealingStage[] = (stagesData || []).map(stage => ({
        ...stage,
        services: (servicesData || [])
          .filter(service => 
            (assignmentsData || []).some(assignment => 
              assignment.stage_id === stage.stage_number && assignment.service_id === service.id
            )
          )
          .map(service => ({
            id: service.id,
            title: service.title,
            description: service.description || '',
            detailed_description: service.detailed_description || undefined,
            image_url: service.image_url || undefined,
            service_type: service.service_type,
            service_status: service.service_status as 'active' | 'coming_soon' | 'inactive',
            sort_order: service.sort_order || 0,
            is_featured: service.is_featured || false,
          } as Service))
          .sort((a, b) => a.sort_order - b.sort_order)
      }));

      setStages(stagesWithServices);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg">{t('common:loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchStagesAndServices}
            className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700"
          >
            {t('common:retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50/50">
      <Navbar />
      
      <main className="pt-16 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-serif text-earth-900 mb-6">
              {t('healingJourneyTitle')}
            </h1>
          </div>

          {/* Stages Accordion */}
          <div className="space-y-4">
            <Accordion type="single" collapsible className="w-full">
              {stages.map((stage) => (
                <AccordionItem 
                  key={stage.stage_number} 
                  value={`stage-${stage.stage_number}`}
                  className={`bg-white border rounded-lg shadow-sm overflow-hidden ${
                    stage.stage_number === 0 
                      ? 'border-red-200 bg-red-50/50' 
                      : getStageColorClasses(stage.color_theme)
                  }`}
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-amber-50/50 transition-colors">
                    <div className="flex items-center space-x-4 text-left w-full">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                        stage.stage_number === 0 ? 'bg-red-500' : getStageColorBg(stage.color_theme)
                      }`}>
                        {stage.stage_number}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-earth-900">
                          Stage {stage.stage_number}: {t(`stages.${stage.stage_number}.title`)}
                        </h3>
                        {stage.stage_number === 0 && (
                          <div className="flex items-center mt-1">
                            <AlertTriangle className="h-4 w-4 text-red-500 mr-1" />
                            <span className="text-sm text-red-600">Emergency Stage</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-2">
                    <div className="ml-16 space-y-6">
                      {/* Stage Description */}
                      {stage.description && (
                        <div>
                          <h4 className="font-semibold text-earth-900 mb-2">{t('stageOverview')}</h4>
                          <div className="text-earth-700 leading-relaxed whitespace-pre-wrap">{t(`stages.${stage.stage_number}.description`)}</div>
                        </div>
                      )}

                      {/* Services Suggested */}
                      <div>
                        <h4 className="font-semibold text-earth-900 mb-2">{t('servicesSuggested')}</h4>
                        <div className="text-earth-700 leading-relaxed italic whitespace-pre-wrap">{t(`servicesSuggestions.${stage.stage_number}`)}</div>
                      </div>

                      {/* Special Message for Stage 0 */}
                      {stage.stage_number === 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-700 font-medium">
                            We work exclusively with licensed psychiatric professionals for individuals in critical mental health states. 
                            Our healing services complement but never replace emergency psychiatric care.
                          </p>
                        </div>
                      )}

                      {/* Services */}
                      {stage.services.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-earth-900 mb-4">{t('availableServices')}</h4>
                          <Accordion type="single" collapsible className="w-full">
                            {stage.services.map((service) => (
                              <AccordionItem 
                                key={service.id} 
                                value={`service-${service.id}`}
                                className="bg-amber-50/30 border border-amber-200 rounded-lg mb-2"
                              >
                                 <AccordionTrigger className="px-3 py-2 sm:px-4 sm:py-3 hover:no-underline hover:bg-amber-100/50 transition-colors">
                                   <div className="flex items-center space-x-2 sm:space-x-3 text-left w-full">
                                     {service.image_url && (
                                       <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0">
                                         <AspectRatio ratio={1}>
                                           <img
                                             src={service.image_url}
                                             alt={service.title}
                                             className="w-full h-full object-cover"
                                           />
                                         </AspectRatio>
                                       </div>
                                     )}
                                     <div className="flex-1 min-w-0">
                                       <h5 className="text-sm sm:text-base font-medium text-earth-900 break-words">
                                         {service.title}
                                         {service.service_status === 'coming_soon' && (
                                           <span className="ml-1 sm:ml-2 text-xs bg-amber-200 text-amber-800 px-1 sm:px-2 py-1 rounded-full block sm:inline mt-1 sm:mt-0">
                                             Coming Soon
                                           </span>
                                         )}
                                         {service.is_featured && (
                                           <span className="ml-1 sm:ml-2 text-xs bg-blue-200 text-blue-800 px-1 sm:px-2 py-1 rounded-full block sm:inline mt-1 sm:mt-0">
                                             Featured
                                           </span>
                                         )}
                                       </h5>
                                     </div>
                                   </div>
                                 </AccordionTrigger>
                                <AccordionContent className="px-4 pb-4 pt-2">
                                  <div className="ml-15">
                                    <div className="text-earth-700 leading-relaxed mb-3 whitespace-pre-wrap">
                                      {service.description}
                                    </div>
                                    {service.detailed_description && (
                                      <div className="text-earth-600 text-sm leading-relaxed mb-3 whitespace-pre-wrap">
                                        {service.detailed_description}
                                      </div>
                                    )}
                                    {service.service_status === 'coming_soon' && (
                                      <div className="mt-3 text-sm text-amber-700 bg-amber-100 rounded-lg p-3">
                                        This service is currently in development. Please check back soon for availability.
                                      </div>
                                    )}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      )}

                      {stage.services.length === 0 && stage.stage_number !== 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-amber-700">No services are currently available for this stage. Check back soon for updates!</p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Healing Stage Survey Section */}
          <section className="mt-12 bg-white rounded-lg border border-amber-100 p-6 shadow-sm text-center" aria-labelledby="healing-survey-heading">
            <h2 id="healing-survey-heading" className="text-2xl font-serif text-earth-900 mb-3">
              {t('healingSurvey.title')}
            </h2>
            <p className="text-earth-700 mb-6">
              {t('healingSurvey.description')}
            </p>
            <TallySurveyEmbed />
          </section>

          {/* Call to Action */}
          <div className="mt-12 text-center bg-white rounded-lg border border-amber-100 p-8 shadow-sm">
            <h2 className="text-2xl font-serif text-earth-900 mb-4">
              {t('readyToBegin')}
            </h2>
            <p className="text-earth-700 mb-6">
              {t('connectWithPractitioners')}
            </p>
            <a
              href="/practitioners"
              className="inline-block bg-amber-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-amber-700 transition-colors text-sm sm:text-base"
            >
              {t('findPractitioners')}
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CertificationsList;
